import { EMPLOYEE_STAFF_POLICY } from "app_config";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Alert, Badge, ToggleSwitch, InputGroup } from "components/common";
import Select from 'react-select';
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { getPositionGroups, createEmployee, provisionGmailAccount } from 'redux/organization/employeeSlice';
import AsyncSelect from 'react-select/async';
import { debounce } from "helpers/debounce"
import MaskedInput from "react-text-mask";
import { useForm, Controller } from "react-hook-form";
import { activeEmployeesDebounce, buildDepartmentOptions } from "helpers/employeeOptionHelper";
import apiAxios from "helpers/apiAxios";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import { closeRequestUserAccount } from "redux/request/userAccountSlice";
import { FaInfoCircle, FaCheckCircle, FaRegCopy } from "react-icons/fa";
import { LuMail } from "react-icons/lu";
import { MdOutlineClose } from 'react-icons/md';

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
import { isSalesTeamTitle } from 'helpers/employeeAccessProfile';
const CreateUserEmployeeForm = ({ open, setOpen, record }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues } = useForm();

  const syncFullName = () => {
    const first = getValues('first_name') || '';
    const last = getValues('last_name') || '';
    const full = [first, last].filter(Boolean).join(' ');
    setValue('full_name', full);
  };

  const syncSalesTeam = (event) => {
    setValue('is_sales', isSalesTeamTitle(event.target.value));
  };
  const { positionGroups } = useSelector((state) => state.organization_employee);
  const [currentSite, setCurrentSite] = useState(record && record.site.id);
  const [provisioning, setProvisioning] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const dispatch = useDispatch();

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  // Provision the Google Workspace / Gmail account now (before Save) and auto-fill
  // the Initial password (generatedPassword) + One-time passcode (backupCodes[0]).
  const handleProvisionEmail = async () => {
    const v = getValues();
    if (!v.name || !v.site_id) {
      toast.error("Employee name and site are required to create the email account.");
      return;
    }
    setProvisioning(true);
    try {
      const res = await dispatch(provisionGmailAccount({
        name: v.name,
        first_name: v.first_name,
        last_name: v.last_name,
        title: v.title,
        site_id: v.site_id
      })).unwrap();
      const d = res.data || {};
      setValue('email_firstcode', d.generated_password || '');
      setValue('email_passcode', d.backup_code || '');
      setEmailResult(d);
      toast.success(`Email account created: ${d.primary_email}`);
    }
    catch (error) {
      // The API error may be a string or an object ({ code, message, retryable }).
      // Coerce to a readable string so we never call String methods on an object.
      const raw = error?.error ?? error?.message;
      const msg = typeof raw === 'string'
        ? raw
        : (raw?.message || raw?.code || "Failed to create email account.");
      toast.error(msg);
    }
    finally {
      setProvisioning(false);
    }
  };

  useEffect(() => {
    if (record) {
      if (record.name) {
        setValue("email", `${record.name.toLocaleLowerCase()}@pebsteel.com`);
      }
      setValue("site_id", record.site?.id);
      setValue("department_id", record?.department?.id);
      setValue("parent_id", record.manager?.id);

      Object.entries(record).forEach(([key, value]) => {
        switch (key) {
          case "gender":
            setValue("gender", value === "Male" ? false : true);
            break;
          case "onboard_date":
            setValue("start_date", value ? new Date(value) : null);
            break;
          default:
            setValue(key, value);
            break;
        }
      });

      // Auto-compute full_name after first_name / last_name are set
      const first = record.first_name || '';
      const last = record.last_name || '';
      const full = [first, last].filter(Boolean).join(' ');
      if (full) setValue('full_name', full);
      if (isSalesTeamTitle(record.title)) setValue('is_sales', true);
    }
  }, [record, setValue]);

  const staffPolicyOptions = useMemo(() => EMPLOYEE_STAFF_POLICY.map((option) => ({ value: option.toLocaleLowerCase(), label: option === "SALES" ? "Sales-Office" : option })), []);
  const defaultSiteOption = useMemo(() => (record && record.site ? { value: record.site.id, label: record.site.name } : null), [record]);
  const defaultDepartmentOption = useMemo(() => (record && record.department ? { value: record.department.id, label: record.department.name } : null), [record]);
  const defaultManagerOption = useMemo(() => (record && record.manager ? { value: record.manager.id, label: record.manager.name } : null), [record]);

  const [departmentOption, setDepartmentOption] = useState(defaultDepartmentOption);

  useEffect(() => {
    dispatch(getPositionGroups());
  }, [dispatch]);

  const onSubmit = async (data) => {
    const employeeData = {
      employee: {
        ...data,
        active: true,
        request_user_account_id: record.id
      }
    };

    try {
      const response = await dispatch(createEmployee(employeeData)).unwrap();
      const payload = response.data;

      if (payload && payload.id !== undefined) {

        await dispatch(closeRequestUserAccount({
          recordId: record.id,
          data: { manual: false }
        })).unwrap();

        handleClose();
      }

    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const debouncedFilterSites = debounce((inputValue, callback) => {
    filterIndexOptions("/organization/sites", inputValue).then((filteredOptions) => {
      callback(filteredOptions);
    });
  }, 300);

  const loadSiteOptions = (inputValue, callback) => {
    debouncedFilterSites(inputValue, callback);
  };

  const debouncedFilterDepartments = debounce((inputValue, callback) => {
    const url = currentSite
      ? `/organization/sites/${currentSite}/departments`
      : '/organization/departments';
    apiAxios.get(`${url}?page=1&per_page=100&name_cont=${inputValue}`)
      .then(r => callback(buildDepartmentOptions(r.data?.rows || [])))
      .catch(() => callback([]));
  }, 300);

  const loadDepartmentOptions = (inputValue, callback) => {
    debouncedFilterDepartments(inputValue, callback);
  };

  const loadManagerOptions = (inputValue, callback) => {
    activeEmployeesDebounce(inputValue, callback);
  };

  const handleSiteChange = (selectOptions) => {
    setValue("site_id", selectOptions.value);
    setDepartmentOption(null);
    setCurrentSite(selectOptions.value);
    loadDepartmentOptions('', (filteredOptions) => filteredOptions);
  };

  const filterIndexOptions = async (url, inputValue) => {
    try {
      const response = await apiAxios.get(`${url}?page=1&per_page=100&name_cont=${inputValue}`);
      const { rows } = response.data;
      return rows.map((item) => {
        const attr = item.attributes || item;
        return { value: attr.id, label: attr.name };
      });
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const positionGroupOptions = positionGroups.map((item) => ({ value: item.id, label: item.name }));

  const fieldRow = (label, children) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
      <div className="tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-text-right">
        {label}
      </div>
      <div className="tw-col-span-12 sm:tw-col-span-7">
        {children}
      </div>
    </div>
  );

  // Row with no label — content aligned under the field column.
  const offsetRow = (children) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2">
      <div className="tw-col-span-12 sm:tw-col-span-3" />
      <div className="tw-col-span-12 sm:tw-col-span-9">
        {children}
      </div>
    </div>
  );

  const fieldError = (error) => error && (
    <span className="tw-text-red-600 tw-text-xs tw-mt-1 tw-block">{error.message}</span>
  );

  return (
    <Drawer
      show={open}
      onHide={handleClose}
      title="Create a new user/employee account"
      width="50%"
      backdropClick={false}
      footer={(
        <>
          <Button type="submit" variant="primary" className="tw-mx-2" form="create-user-employee-form">Save</Button>
          <Button variant="danger" onClick={handleClose}><MdOutlineClose /> Cancel</Button>
        </>
      )}
    >
      <Alert type="info" className="tw-mb-3">
        Please fill in the form below to create a new user/employee account. <strong>After the user/employee is created, the request will be automatically closed and response sent to submiter.</strong>
      </Alert>

      <form id="create-user-employee-form" onSubmit={handleSubmit(onSubmit)}>
        {fieldRow('Name', (
          <>
            <input
              type='text'
              placeholder='Name'
              className={INPUT_CLASSES}
              {...register('name', {
                required: 'Name must be required.'
              })}
            />
            {fieldError(errors.name)}
          </>
        ))}

        {fieldRow('First name', (
          <>
            <input
              type='text'
              placeholder='First name'
              className={INPUT_CLASSES}
              {...register('first_name', {
                required: 'First name is required.',
                onChange: syncFullName,
              })}
            />
            {fieldError(errors.first_name)}
          </>
        ))}

        {fieldRow('Last name', (
          <>
            <input
              type='text'
              placeholder='Last name'
              className={INPUT_CLASSES}
              {...register('last_name', {
                required: 'Last name is required.',
                onChange: syncFullName,
              })}
            />
            {fieldError(errors.last_name)}
          </>
        ))}

        {fieldRow('Full name', (
          <>
            <input
              type='text'
              placeholder='Auto-generated'
              className={`${INPUT_CLASSES} tw-bg-slate-50`}
              readOnly
              {...register('full_name', { required: 'Full name is required.' })}
            />
            {fieldError(errors.full_name)}
          </>
        ))}

        {fieldRow('Title', (
          <>
            <input
              type='text'
              placeholder='Title'
              className={INPUT_CLASSES}
              {...register('title', {
                required: 'Title must be required.',
                onChange: syncSalesTeam,
              })}
            />
            {fieldError(errors.title)}
          </>
        ))}

        {fieldRow('Email', (
          <>
            <input
              type='email'
              placeholder='Email'
              className={INPUT_CLASSES}
              {...register('email', {
                required: 'Email must be required.'
              })}
            />
            {fieldError(errors.email)}
          </>
        ))}

        {fieldRow('Phone number', (
          <input
            type='text'
            placeholder='Phone number'
            className={INPUT_CLASSES}
            {...register('phone_number')}
          />
        ))}

        {fieldRow('Manager', (
          <>
            <Controller
              control={control}
              name="parent_id"
              render={() => (
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  defaultValue={defaultManagerOption}
                  loadOptions={loadManagerOptions}
                  closeMenuOnSelect={true}
                  placeholder='<Select manager>'
                  classNamePrefix="react-select"
                  className="tw-w-full"
                  onChange={selectedOption => setValue('parent_id', selectedOption.value)}
                />
              )}
            />
            {fieldError(errors.parent_id)}
          </>
        ))}

        {offsetRow(
          <Controller
            control={control}
            name='gender'
            defaultValue={false}
            render={({ field }) => (
              <ToggleSwitch
                value={!!field.value}
                onChange={(checked) => setValue('gender', checked)}
                label='Female'
              />
            )}
          />
        )}

        {offsetRow(
          <Controller
            control={control}
            name='is_sales'
            defaultValue={false}
            render={({ field }) => (
              <ToggleSwitch
                value={!!field.value}
                onChange={(checked) => setValue('is_sales', checked)}
                label='Sales team'
              />
            )}
          />
        )}

        {fieldRow('Start date', (
          <Controller
            control={control}
            name="start_date"
            render={({ field }) => (
              <DatePicker
                selected={field.value}
                onChange={d => field.onChange(adjustToNoon(d))}
                className={INPUT_CLASSES}
                placeholderText="DD-MM-YYYY"
                dateFormat="dd-MM-yyyy"
                fixedHeight
              />
            )}
          />
        ))}

        {fieldRow('HR Code', (
          <input
            type='text'
            placeholder='HR Code'
            className={INPUT_CLASSES}
            {...register('hr_code')}
          />
        ))}

        {fieldRow('Birthday', (
          <Controller
            control={control}
            name='birthday'
            render={({ field }) => (
              <MaskedInput
                mask={[/\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                className={INPUT_CLASSES}
                guide={false}
                placeholder="DD-MM-YYYY"
                {...field}
              />
            )}
          />
        ))}

        {fieldRow('Position group', (
          <>
            <Controller
              control={control}
              name='position_group_id'
              rules={{ required: "Position group is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  closeMenuOnSelect={true}
                  options={positionGroupOptions}
                  value={positionGroupOptions.find(option => option.value === field.value) || null}
                  placeholder='Select...'
                  classNamePrefix='react-select'
                  onChange={selectedOption => setValue('position_group_id', selectedOption.value)}
                />
              )}
            />
            {fieldError(errors.position_group_id)}
          </>
        ))}

        {fieldRow('Staff policy', (
          <>
            <Controller
              control={control}
              name='staff_policy'
              rules={{ required: "Staff policy is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  closeMenuOnSelect={true}
                  options={staffPolicyOptions}
                  value={staffPolicyOptions.find(option => option.value === field.value) || null}
                  placeholder='Select...'
                  classNamePrefix='react-select'
                  onChange={selectedOption => setValue('staff_policy', selectedOption.value)}
                />
              )}
            />
            {fieldError(errors.staff_policy)}
          </>
        ))}

        {fieldRow('Site', (
          <>
            <Controller
              control={control}
              name='site_id'
              render={() => (
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  defaultValue={defaultSiteOption}
                  loadOptions={loadSiteOptions}
                  closeMenuOnSelect={true}
                  placeholder='Select...'
                  classNamePrefix='react-select'
                  className='tw-w-full'
                  onChange={handleSiteChange}
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: 'Site must be required'
                }
              }}
            />
            {fieldError(errors.site_id)}
          </>
        ))}

        {fieldRow('Deparment', (
          <>
            <Controller
              control={control}
              name='department_id'
              render={() => (
                <AsyncSelect
                  key={currentSite}
                  cacheOptions
                  defaultOptions
                  value={departmentOption}
                  loadOptions={loadDepartmentOptions}
                  closeMenuOnSelect={true}
                  placeholder='Select...'
                  classNamePrefix='react-select'
                  className='tw-w-full'
                  onChange={(selectOptions) => {
                    setValue("department_id", selectOptions.value)
                    setDepartmentOption(selectOptions)
                  }}
                />
              )}
            />
            {fieldError(errors.department_id)}
          </>
        ))}

        {offsetRow(
          <>
            <Controller
              control={control}
              name='create_user_account'
              defaultValue={false}
              render={({ field }) => (
                <ToggleSwitch
                  value={!!field.value}
                  onChange={(checked) => setValue('create_user_account', checked)}
                  label='Create user account'
                />
              )}
            />
            <span className='tw-text-slate-500 tw-text-[11px] tw-inline-flex tw-items-center tw-gap-1 tw-mt-1'>
              <FaInfoCircle /> Create a user account to access the Portal system.
            </span>
          </>
        )}

        {record?.purpose_create_emails && (
          <div className='tw-mt-4 tw-mb-2 tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-bg-slate-50'>
            {/* Header */}
            <div className='tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-border-b tw-border-solid tw-border-slate-200'>
              <span className='tw-font-semibold tw-text-slate-900 tw-inline-flex tw-items-center tw-text-sm'>
                <LuMail className='tw-mr-2' style={{ color: '#EA4335' }} /> Email account
                <span className='tw-text-slate-500 tw-font-normal tw-ml-2 tw-text-[11px]'>Google Workspace</span>
              </span>
              {emailResult?.primary_email && (
                <Badge variant='success' size='sm'>
                  <FaCheckCircle className='tw-mr-1' /> Created
                </Badge>
              )}
            </div>

            <div className='tw-p-3'>
              <div className='tw-text-slate-600 tw-mb-3 tw-text-xs'>
                <FaInfoCircle className='tw-text-yellow-500 tw-mr-1' />
                Provision the mailbox, then hand the <strong>Initial password</strong> and{' '}
                <strong>One-time passcode</strong> to the user.
              </div>

              {/* Provision action + result */}
              <div className='tw-mb-3'>
                <Button
                  variant={emailResult ? 'ghost' : 'primary'}
                  size='sm'
                  disabled={provisioning}
                  loading={provisioning}
                  onClick={handleProvisionEmail}
                >
                  {provisioning ? 'Creating…' : (
                    <><LuMail className='tw-mr-2' /> {emailResult ? 'Re-create email account' : 'Create email account'}</>
                  )}
                </Button>
                {emailResult?.primary_email && (
                  <div className='tw-text-green-600 tw-mt-2 tw-inline-flex tw-items-center tw-text-sm'>
                    <FaCheckCircle className='tw-mr-1' /> {emailResult.primary_email}
                    {emailResult.sku_label && (
                      <span className='tw-text-slate-500 tw-ml-1'>· {emailResult.sku_label}</span>
                    )}
                  </div>
                )}
              </div>

              <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
                <div className="tw-col-span-12 sm:tw-col-span-4 tw-text-xs tw-font-medium tw-text-slate-700 sm:tw-text-right">
                  Initial password
                </div>
                <div className="tw-col-span-12 sm:tw-col-span-8">
                  <InputGroup>
                    <input
                      type='text'
                      placeholder='Initial email password'
                      className={INPUT_CLASSES}
                      {...register('email_firstcode', {
                        required: 'Initial email password must be required.'
                      })}
                    />
                    <Button
                      variant='ghost'
                      title='Copy'
                      onClick={() => copyToClipboard(getValues('email_firstcode'))}
                    >
                      <FaRegCopy />
                    </Button>
                  </InputGroup>
                  {fieldError(errors.email_firstcode)}
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
                <div className="tw-col-span-12 sm:tw-col-span-4 tw-text-xs tw-font-medium tw-text-slate-700 sm:tw-text-right">
                  One-time passcode
                </div>
                <div className="tw-col-span-12 sm:tw-col-span-8">
                  <InputGroup>
                    <input
                      type='text'
                      placeholder='Email passcode / One-time passcode (OTP)'
                      className={INPUT_CLASSES}
                      {...register('email_passcode', {
                        required: 'Email passcode / One-time passcode (OTP) must be required.'
                      })}
                    />
                    <Button
                      variant='ghost'
                      title='Copy'
                      onClick={() => copyToClipboard(getValues('email_passcode'))}
                    >
                      <FaRegCopy />
                    </Button>
                  </InputGroup>
                  {fieldError(errors.email_passcode)}
                </div>
              </div>

              {/* All recovery/backup codes — read-only, so HR can hand the full set
                  to the user (the passcode field holds only the first). */}
              {emailResult?.backup_codes?.length > 1 && (
                <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-items-start">
                  <div className="tw-col-span-12 sm:tw-col-span-4 tw-text-xs tw-font-medium tw-text-slate-700 sm:tw-text-right">
                    All backup codes
                  </div>
                  <div className="tw-col-span-12 sm:tw-col-span-8">
                    <div className='tw-flex tw-items-start tw-gap-2'>
                      <code className='tw-flex-1 tw-p-2 tw-rounded-sm tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-text-slate-600 tw-text-[11px]' style={{ wordBreak: 'break-word' }}>
                        {emailResult.backup_codes.join('  ·  ')}
                      </code>
                      <Button
                        variant='ghost'
                        size='sm'
                        title='Copy all'
                        onClick={() => copyToClipboard(emailResult.backup_codes.join('\n'))}
                      >
                        <FaRegCopy />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </form>
    </Drawer>
  )
};

CreateUserEmployeeForm.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  record: PropTypes.object.isRequired,
};

export default CreateUserEmployeeForm;
